<template>
  <div
    ref="containerRef"
    class="resizable_panel_layout"
    :class="{ resizable_panel_layout_horizontal: isHorizontal }"
    :style="containerStyle"
  >
    <div
      class="resizable_panel_primary"
      :class="primaryPanelClass"
      :style="primaryPanelStyle"
    >
      <slot name="primary">
        <slot name="left" />
      </slot>
      <div
        class="resizable_panel_handle"
        :class="handleClass"
        @mousedown="startResize"
      />
    </div>

    <div class="resizable_panel_secondary" :style="secondaryPanelStyle">
      <slot name="secondary">
        <slot name="right" />
      </slot>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";
import { chartAlignment } from "@/src/constants/chartConstants.js";

const props = defineProps({
  storageKey: {
    type: String,
    required: true,
  },
  orientation: {
    type: String,
    default: chartAlignment.VERTICAL,
    validator: (value) =>
      [chartAlignment.VERTICAL, chartAlignment.HORIZONTAL].includes(value),
  },
  defaultWidth: {
    type: Number,
    default: 400,
  },
  minWidth: {
    type: [Number, String],
    default: 300,
  },
  maxWidth: {
    type: [Number, String],
    default: 700,
  },
  minWidthRight: {
    type: [Number, String],
    default: 300,
  },
  height: {
    type: String,
    default: null,
  },
  defaultSizePercent: {
    type: Number,
    default: 60,
  },
  minPrimarySizePercent: {
    type: Number,
    default: 40,
  },
  maxPrimarySizePercent: {
    type: Number,
    default: 80,
  },
  minSecondarySizePercent: {
    type: Number,
    default: 20,
  },
});

const containerRef = ref(null);
const panelSize = ref(props.defaultWidth);
const panelSizePercent = ref(props.defaultSizePercent);
const isResizing = ref(false);
const startPos = ref(0);
const startSize = ref(0);
const containerDimension = ref(0);
let resizeObserver = null;

const isHorizontal = computed(
  () => props.orientation === chartAlignment.HORIZONTAL,
);

const primaryPanelClass = computed(() => ({
  border_right: !isHorizontal.value,
  border_bottom: isHorizontal.value,
}));

const handleClass = computed(() => ({
  resizing: isResizing.value,
  resizable_panel_handle_horizontal: isHorizontal.value,
}));

const containerStyle = computed(() => {
  const style = {};
  if (props.height) {
    style.height = props.height;
    style.minHeight = props.height;
    style.maxHeight = props.height;
  }
  return style;
});

const getContainerDimension = () => {
  try {
    if (!containerRef.value)
      return isHorizontal.value ? 400 : window.innerWidth;
    return isHorizontal.value
      ? containerRef.value.offsetHeight
      : containerRef.value.offsetWidth;
  } catch (error) {
    console.error(
      "An error occurred while getting container dimension: ",
      error,
    );
  }
};

const updateContainerDimension = () => {
  try {
    if (containerRef.value) {
      containerDimension.value = isHorizontal.value
        ? containerRef.value.offsetHeight
        : containerRef.value.offsetWidth;
    }
  } catch (error) {
    console.error(
      "An error occurred while updating container dimension: ",
      error,
    );
  }
};

const getPixelValue = (value) => {
  try {
    if (typeof value === "number") {
      return value;
    }
    const dimension = getContainerDimension();
    return Math.max(dimension / 12, 150);
  } catch (error) {
    console.error("An error occurred while getting pixel value: ", error);
  }
};

const getCssValue = (value) => {
  try {
    if (typeof value === "string") {
      return value;
    }
    return `${value}px`;
  } catch (error) {
    console.error("An error occurred while getting css value: ", error);
  }
};

const minSizeValue = computed(() => {
  if (isHorizontal.value) {
    const containerHeight = getContainerDimension();
    return (containerHeight * props.minPrimarySizePercent) / 100;
  }
  return getPixelValue(props.minWidth);
});

const minSecondaryValue = computed(() => {
  if (isHorizontal.value) {
    const containerHeight = getContainerDimension();
    return (containerHeight * props.minSecondarySizePercent) / 100;
  }
  return getPixelValue(props.minWidthRight);
});

const maxSizeValue = computed(() => {
  const dimension = getContainerDimension();
  if (isHorizontal.value) {
    return (dimension * props.maxPrimarySizePercent) / 100;
  }
  const maxFromSecondaryMin = dimension - minSecondaryValue.value;
  if (typeof props.maxWidth === "number") {
    return Math.min(props.maxWidth, maxFromSecondaryMin);
  }
  return maxFromSecondaryMin;
});

const minSizeStyle = computed(() => {
  if (isHorizontal.value) {
    return `${props.minPrimarySizePercent}%`;
  }
  return getCssValue(props.minWidth);
});

const minSecondaryStyle = computed(() => {
  if (isHorizontal.value) {
    return `${props.minSecondarySizePercent}%`;
  }
  return getCssValue(props.minWidthRight);
});

const maxSizeStyle = computed(() => {
  if (isHorizontal.value) {
    return `${props.maxPrimarySizePercent}%`;
  }
  const minSecCss = minSecondaryStyle.value;
  if (typeof props.maxWidth === "string") {
    return `min(${props.maxWidth}, calc(100% - ${minSecCss}))`;
  }
  return `min(${props.maxWidth}px, calc(100% - ${minSecCss}))`;
});

const primaryPanelStyle = computed(() => {
  if (isHorizontal.value) {
    return {
      height: `${panelSizePercent.value}%`,
      minHeight: minSizeStyle.value,
      maxHeight: maxSizeStyle.value,
      width: "100%",
    };
  }
  return {
    width: `${panelSize.value}px`,
    minWidth: minSizeStyle.value,
    maxWidth: maxSizeStyle.value,
    height: "100%",
  };
});

const secondaryPanelStyle = computed(() => {
  if (isHorizontal.value) {
    return {
      minHeight: minSecondaryStyle.value,
      width: "100%",
    };
  }
  return {
    minWidth: minSecondaryStyle.value,
    height: "100%",
  };
});

const loadSizeFromStorage = () => {
  try {
    const stored = localStorage.getItem(props.storageKey);
    if (stored) {
      const size = parseFloat(stored);
      if (!isNaN(size)) {
        if (isHorizontal.value) {
          if (
            size >= props.minPrimarySizePercent &&
            size <= props.maxPrimarySizePercent
          ) {
            panelSizePercent.value = size;
          }
        } else {
          if (size >= minSizeValue.value && size <= maxSizeValue.value) {
            panelSize.value = size;
          }
        }
      }
    }
  } catch (error) {
    console.error("Error loading panel size from localStorage:", error);
  }
};

const saveSizeToStorage = () => {
  try {
    const valueToStore = isHorizontal.value
      ? panelSizePercent.value.toString()
      : panelSize.value.toString();
    localStorage.setItem(props.storageKey, valueToStore);
  } catch (error) {
    console.error("Error saving panel size to localStorage:", error);
  }
};

const startResize = (event) => {
  isResizing.value = true;
  startPos.value = isHorizontal.value ? event.clientY : event.clientX;
  startSize.value = isHorizontal.value
    ? panelSizePercent.value
    : panelSize.value;
  document.addEventListener("mousemove", onResize);
  document.addEventListener("mouseup", stopResize);
  document.body.style.cursor = isHorizontal.value ? "row-resize" : "col-resize";
  document.body.style.userSelect = "none";
};

const onResize = (event) => {
  if (!isResizing.value) return;

  if (isHorizontal.value) {
    const containerHeight = getContainerDimension();
    const deltaY = event.clientY - startPos.value;
    const deltaPercent = (deltaY / containerHeight) * 100;
    let newPercent = startSize.value + deltaPercent;

    newPercent = Math.max(
      props.minPrimarySizePercent,
      Math.min(props.maxPrimarySizePercent, newPercent),
    );
    panelSizePercent.value = newPercent;
  } else {
    const deltaX = event.clientX - startPos.value;
    let newWidth = startSize.value + deltaX;

    newWidth = Math.max(
      minSizeValue.value,
      Math.min(maxSizeValue.value, newWidth),
    );
    panelSize.value = newWidth;
  }
};

const stopResize = () => {
  if (isResizing.value) {
    isResizing.value = false;
    saveSizeToStorage();
  }
  document.removeEventListener("mousemove", onResize);
  document.removeEventListener("mouseup", stopResize);
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
};

onMounted(() => {
  loadSizeFromStorage();

  nextTick(() => {
    updateContainerDimension();
  });

  resizeObserver = new ResizeObserver(() => {
    updateContainerDimension();
  });

  if (containerRef.value) {
    resizeObserver.observe(containerRef.value);
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
  document.removeEventListener("mousemove", onResize);
  document.removeEventListener("mouseup", stopResize);
});
</script>

<style scoped>
.resizable_panel_layout {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.resizable_panel_layout_horizontal {
  flex-direction: column;
}

.resizable_panel_primary {
  flex-shrink: 0;
  overflow: auto;
  position: relative;
  box-sizing: border-box;
  width: 100%;
}

.resizable_panel_layout_horizontal .resizable_panel_primary {
  width: 100%;
}

.resizable_panel_handle {
  position: absolute;
  top: 0;
  right: -3px;
  width: 6px;
  height: 100%;
  cursor: col-resize;
  z-index: 10;
  background: transparent;
}

.resizable_panel_handle:hover,
.resizable_panel_handle.resizing {
  background: var(--white-opacity-8);
}

.resizable_panel_handle_horizontal {
  top: auto;
  bottom: -3px;
  right: 0;
  width: 100%;
  height: 6px;
  cursor: row-resize;
}

.resizable_panel_secondary {
  flex: 1;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
}
</style>
